import { LatLngLiteral } from "leaflet";

import { store } from "../../redux/store";
import {
	defaultLanguage,
	MapObjectLanguageFields,
	pickMapObjectLanguageFields,
	ValueLanguage,
} from "../../assets/languages/langs";
import SearchType from "../../types/SearchType";
import { Base, Language } from "..";
import { createLogger } from "../../utils";
import {
	decompressPointsAndSegments,
	decompressRoute,
	decompressRouteSegment,
} from "../../utils/decompressRoute.util";
import SubscriptionPool from "../../redux/services/SubscriptionPool";

const logger = createLogger({ name: "Map" });

class Map extends Base {
	/**
	 * @deprecated Questionable code, use search2
	 */
	public static async deprecatedSearch(
		query: Map.Search.Query,
		params: Map.Search.Options,
	) {
		const res = await this.request((prpc) =>
			prpc.theirsModel.map.search(query, params),
		);

		if (!Array.isArray(res?.objects)) return res;

		const currentLang =
			store.getState().settings.map.mapLanguage || defaultLanguage;

		const processedData: Map.Search.DeprecatedResponse[] = res.objects.map(
			(item) => ({
				type: item.type,
				...item.properties.language[currentLang],
				coordinates: item.properties.geoCentroid,
				bbox: item.properties.bbox,
				geometry: item.properties.geometry,
			}),
		);

		return processedData;
	}

	public static normalizeItem(object: any, valueLang?: ValueLanguage) {
		const { id, type } = object;
		const preferredLang = (valueLang || defaultLanguage) as Language;
		const picked = pickMapObjectLanguageFields<MapObjectLanguageFields>(
			object.properties?.language,
			preferredLang,
		);
		const address = picked?.fields;
		const language = picked?.lang ?? preferredLang;
		const { properties } = object;

		const item: Map.Search.Object = {
			id,
			type,
			language,
			bbox:
				properties?.bbox && properties.bbox?.length
					? properties.bbox
					: [properties?.geoCentroid, properties?.geoCentroid],
			geometry: properties?.geometry || {
				type: "Point",
				coordinates: properties?.geoCentroid,
			},
			coordinates: properties?.geoCentroid,

			country: address?.country || "",
			countryCode: address?.countryCode || "",

			settlement: address?.settlement || "",
			settlementClass: address?.settlementClass || "",
			settlementType: address?.settlementType || "",
			district: address?.district || "",
			region: address?.region || "",

			sector: address?.sector,

			street: address?.street || "",
			streetType: address?.streetType || "",
			houses: (address?.streetHouses ?? []) as Map.Search.House[],

			name: address?.name || "",

			number: address?.number || "",

			house: address?.house || "",
			entrance: address?.entrance || "",

			customHouse: address?.customHouse || "",
			customStreet: address?.customStreet || "",
			title: address?.title || "",
			searchType: properties?.searchType || "",
			placeId: properties?.placeId || "",
		};

		return item;
	}

	public static async search(
		query: Map.Search.Query,
		options: Map.Search.Options,
	): Promise<Map.Search.Response> {
		const response = await this.request((prpc) =>
			prpc.theirsModel.map.search(query, this.toSearchOptions(options)),
		);

		return {
			resolution: response.resolution,
			objects: response.objects.map((object) => {
				const { id, type } = object;
				const preferredLang = (options?.language ||
					defaultLanguage) as Language;
				const picked =
					pickMapObjectLanguageFields<MapObjectLanguageFields>(
						object.properties?.language,
						preferredLang,
					);
				const address = picked?.fields;
				const language = picked?.lang ?? preferredLang;
				const { properties } = object;

				const item: Map.Search.Object = {
					id,
					type,
					language,
					bbox:
						properties?.bbox && properties.bbox?.length
							? properties.bbox
							: [properties.geoCentroid, properties.geoCentroid],
					geometry: properties?.geometry || {
						type: "Point",
						coordinates: properties?.geoCentroid,
					},
					coordinates: properties?.geoCentroid,

					country: address?.country || "",
					countryCode: address?.countryCode || "",

					settlement: address?.settlement || "",
					settlementClass: address?.settlementClass || "",
					settlementType: address?.settlementType || "",
					district: address?.district || "",
					region: address?.region || "",

					sector: address?.sector,

					street: address?.street || "",
					streetType: address?.streetType || "",
					houses: (address?.streetHouses ?? []) as Map.Search.House[],

					name: address?.name || "",

					number: address?.number || "",

					house: address?.house || "",
					entrance: address?.entrance || "",

					customHouse: address?.customHouse || "",
					customStreet: address?.customStreet || "",
					title: address?.title || "",
					searchType: properties?.searchType || "",
					placeId: properties?.placeId || "",
				};

				return item;
			}),
		};
	}

	public static async searchStreet(
		callback: (data: Map.Search.StreetResponse) => void,
		callbackError?: (data: any) => void,
	): Promise<{
		unsubscribe: () => Promise<void>;
		update: (options: Map.Search.StreetOptions) => Promise<void>;
	}> {
		const subscription = await SubscriptionPool.add((prpc) =>
			prpc.theirsModel.map.subscribeSearchStreet({
				ping: () => true,
				onEvent: (events: Map.Search.StreetResponse) => {
					if (callback) callback(events);
					else logger.info("[Map][searchStreet] events", events);
				},
				onError: (error) => {
					if (callbackError) callbackError(error);
					logger.error(error);
				},
			}),
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (options: Map.Search.StreetOptions) =>
				subscription.update(options),
		};
	}

	private static toSearchOptions(options: Map.Search.Options) {
		const retval = {
			customerId: options.customerId,
			searchType: options.searchType,
			lang: options.language,
			limit: options.limit,
			country: options.country,
			near: options.near,
			responseOrder: options.responseOrder,
			responseOrderBySettlement: options.responseOrderBySettlement,
			taxiServiceId: options.taxiServiceId,
			mainSettlement: options.mainSettlement,
			lite: options.lite,
			placeId: options.placeId,
		};

		logger.info("[Map] toSearchOptions:", {
			options,
			retval,
		});

		return retval;
	}

	public static async getSegmentsAndPointsRoute(
		points: LatLngLiteral[],
		decompres = false,
	) {
		try {
			if (!points?.length) return null;
			if (points.length === 1) return null;

			const res = await this.request(
				(prpc) => prpc.theirsModel.map.route(points || []),
				{ silent: false, error: true },
			);

			const result = decompres ? decompressPointsAndSegments(res) : res;

			logger.log("[Order Map] getSegmentsAndPointsRoute:", {
				points,
				result,
			});
			return result;
		} catch (err: any) {
			logger.error("[Map] Error getSegmentsAndPointsRoute:", err);
			return null;
		}
	}

	static async getOrderRouteById(id?: number): Promise<any> {
		if (!id) return null;
		try {
			const res = await this.request(
				(prpc) => prpc.theirsModel.orders.getOrderRouteById(id),
				{ silent: false, error: true },
			);

			logger.info("[Order Map.getOrderRouteById] request ", {
				id,
				res,
			});

			return decompressRouteSegment(res);
		} catch (err: any) {
			logger.error("[Map] Error getOrderRouteById:", err);
			return null;
		}
	}

	public static async calcRoute(points: LatLngLiteral[], decompres = false) {
		try {
			if (!points?.length) return null;
			if (points.length === 1) return null;

			const res = await this.request(
				(prpc) => prpc.theirsModel.map.route(points || []),
				{ silent: false, error: true },
			);

			if (decompres) {
				const route = decompressRoute(res);
				return route;
			}

			return res;
		} catch (err: any) {
			logger.error("[Map] Error calcRoute:", err);
			return null;
		}
	}
}

declare namespace Map {
	namespace Search {
		type Query = string;

		/**
		 * @deprecated Used in deprecated search
		 */
		interface DeprecatedResponse {
			type: Type;
			language: Language;
			bbox: LatLngLiteral[];
			geometry?: {
				type: string;
				coordinates:
					| LatLngLiteral[]
					| LatLngLiteral[][]
					| LatLngLiteral;
			};
			coordinates: string;
			country: string;
			countryCode: string;

			settlement?: string;
			settlementType?: string;
			region?: string;
			district?: string;

			street?: string;
			streetType?: string;
			streetHouses?: any[];

			number?: string;
			house?: string;
			entrance?: string;
			customHouse?: string;

			name?: string;
			customStreet?: string;
			title?: string;
		}

		interface PolygonGeometry {
			type: "Polygon";
			coordinates: LatLngLiteral[][];
		}

		interface MultiPolygonGeometry {
			type: "MultiPolygon";
			coordinates: LatLngLiteral[][][];
		}

		interface LineGeometry {
			type: "LineString";
			coordinates: LatLngLiteral[];
		}

		interface MultilineGeometry {
			type: "MultiLineString";
			coordinates: LatLngLiteral[][];
		}

		interface PointGeometry {
			type: "Point";
			coordinates: LatLngLiteral;
		}

		type Geometry =
			| PointGeometry
			| LineGeometry
			| MultilineGeometry
			| PolygonGeometry
			| MultiPolygonGeometry;

		interface House {
			id: string;
			number: string;
		}

		interface Object {
			searchType?: string;
			placeId?: string;
			streetHouses?: any;
			id: string;
			type: Type;
			language: Language;
			bbox: LatLngLiteral[];
			geometry: Geometry;
			coordinates: LatLngLiteral;

			country: string;
			countryCode: string;

			settlement?: string;
			settlementClass?: string;
			settlementType?: string;

			region?: string;

			district?: string;

			sector?: { id: number; name: Record<Language, string> } | null;
			street?: string;
			streetType?: string;

			houses?: House[];

			name?: string;
			number?: string;
			entrance?: string;
			house?: string;
			customHouse?: string;
			customStreet?: string;
			title?: string;
		}

		interface Response {
			resolution: "visicom";
			// eslint-disable-next-line @typescript-eslint/ban-types
			objects: Object[];
		}

		interface Options {
			customerId?: number;
			searchType: Type | Type[];
			language?: Language;
			country?: string;
			near?: {
				point: LatLngLiteral;
				radius?: number;
				zoom?: number;
			};
			/**
			 * Limit of objects in response.
			 */
			limit?: number;
			/**
			 * Return only objects of the specified taxi service.
			 * Used for the 'localObject' search type
			 */
			taxiServiceId?: number;
			/**
			 * Response order of objects
			 */
			responseOrder?: ResponseOrderType | ResponseOrderType[];
			/**
			 * Sorts by settlements given the order of 'responseOrder'
			 */
			responseOrderBySettlement?: boolean;

			mainSettlement?: string;
			lite?: boolean;
			/**
			 * Already-known Nominatim place_id of the picked street segment.
			 * When provided together with `searchType: "street"` and `limit: 1`,
			 * the backend skips the full re-search and goes straight to the
			 * houses-by-parent-ids fast path.
			 */
			placeId?: string;
		}

		interface StreetOptions {
			query: string;
			row: string | number;
			orderId?: number;
			customerId?: number;
			taxiServiceId?: number;
			searchType: Map.Search.Options["searchType"];
			language?: Map.Search.Options["language"];
			near?: {
				point: {
					lat: number;
					lng: number;
				};
				radius?: number;
				zoom?: number;
			};
			lang?: Map.Search.Options["language"];
			responseOrder?: Map.Search.Options["responseOrder"];
			responseOrderBySettlement?: boolean;
			mainSettlement?: string;
			lite?: boolean;
			limit?: number;
			offset?: number;
		}
		type MapSearchStreetType =
			| "key"
			| "favorite"
			| "addresses"
			| "streets"
			| "houses"
			| "localObject"
			| "localObjectGrouped"
			| "settlement";

		interface SearchStreetHouses {
			id: string | number;
			streetHouses: House[];
		}

		interface StreetResponse {
			key: string;
			row: number;
			type: MapSearchStreetType;
			orderId?: number;
			data: Map.Search.Object[] | Map.Search.SearchStreetHouses[];
		}

		type ResponseOrderType =
			| "address"
			| "street"
			| "country"
			| "settlement"
			| "settlementDistrict"
			| "region"
			| "district"
			| "road"
			| "adminLevel3"
			| "adminPlace"
			| "localObject"
			| "object";

		type Type = "price_zone" | "parking" | SearchType;
	}
}

export default Map;
